import React, { useState, useEffect, useRef } from "react";
import { Upload, FileText, Send, AlertCircle } from "lucide-react";
import type { UserTask, SubmitTaskInput } from "../../../types/task";
import Modal from "../../../components/ui/Modal";

interface SubmitTaskModalProps {
  isOpen: boolean;
  task: UserTask | null;
  onClose: () => void;
  onSubmit: (input: SubmitTaskInput) => void;
}

export const SubmitTaskModal: React.FC<SubmitTaskModalProps> = ({ isOpen, task, onClose, onSubmit }) => {
  const [textContent, setTextContent] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && task) {
      if (task.submission && task.submission.status === "REJECTED" && task.submission.content) {
        setTextContent(task.submission.content);
      } else {
        setTextContent("");
      }
      setSelectedFile(null);
      setError("");
    }
  }, [isOpen, task]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Dosya boyutu 10MB'dan küçük olmalıdır");
        return;
      }
      setSelectedFile(file);
      setError("");
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task) return;

    if (task.itemType === "TEXT") {
      if (!textContent.trim()) {
        setError("Lütfen içerik yazın");
        return;
      }
    } else {
      setError("Dosya yükleme geçici olarak kapalı. Şimdilik yalnızca metin gönderimi yapılabilir.");
      return;
    }

    const input: SubmitTaskInput = {
      taskId: task.taskId,
    };

    if (task.itemType === "TEXT") {
      input.content = textContent.trim();
    }

    onSubmit(input);
    onClose();
  };

  if (!task) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Görev Gönderimi: ${task.title}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="p-6 space-y-5">
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <h3 className="text-sm font-medium text-purple-900 mb-2">Görev Açıklaması:</h3>
            <p className="text-sm text-purple-800">{task.description}</p>
          </div>

          {task.submission && task.submission.status === "REJECTED" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-sm font-semibold text-red-900 mb-2">⚠️ Önceki Gönderiminiz Reddedildi</h3>
              <p className="text-sm text-red-800 mb-2">
                Reddedilen içeriğiniz aşağıda yüklenmiştir. Düzenleyerek tekrar gönderebilirsiniz.
              </p>
              {task.submission.reviewNote && (
                <div className="mt-2 p-2 bg-red-100 rounded">
                  <p className="text-xs font-medium text-red-900 mb-1">Geri Bildirim:</p>
                  <p className="text-xs text-red-800">{task.submission.reviewNote}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              {task.itemType === "TEXT"
                ? "Yalnızca yazılı içerik gönderimi yapılabilir."
                : "Dosya yükleme geçici olarak kapalı. Bu görev için gönderim yapılamaz."}
            </p>
          </div>

          {task.itemType === "TEXT" && (
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                İçeriğinizi Yazın <span className="text-red-500">*</span>
              </label>
              <textarea
                id="content"
                value={textContent}
                onChange={(e) => {
                  setTextContent(e.target.value);
                  setError("");
                }}
                rows={10}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${error ? "border-red-500" : "border-gray-300"
                  }`}
                placeholder="Görev için gerekli içeriği buraya yazın..."
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">{textContent.length} karakter</span>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Son Gönderim Tarihi</p>
                <p className="text-sm text-yellow-800">
                  {new Date(task.endDate).toLocaleDateString("tr-TR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
            İptal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors inline-flex items-center gap-2">
            <Send className="w-4 h-4" />
            Gönder
          </button>
        </div>
      </form>
    </Modal>
  );
};